// Estado único de la sesión del usuario autenticado (una sola consulta a /auth/me compartida por toda la app).
//   loading         → restaurando (token → perfil). No se muestra la aplicación.
//   authenticated   → perfil válido (uid, rol, sucursal). Recién aquí la app puede construir menú y pantallas.
//   unauthenticated → sin token, token vencido o rechazado (401): se limpia la sesión y se va al login.
//   error           → no se pudo cargar el perfil tras reintentar (red/servidor): se ofrece "Reintentar".
// Nunca se "rellena" un perfil por defecto ante un error (antes un fallo dejaba al administrador como cajero sin uid).
import { fetchMe } from './session';
import { getToken, clearSession, avisarSesionVencida, SESION_VENCIDA } from './api';
import { diag } from './diagnostico';

export const ERROR_SESION = 'No fue posible cargar la información de tu sesión.';
let ESPERAS_MS = [0, 1000, 3000]; // 3 intentos como máximo; sin bucles

const PERFIL_VACIO = { uid: null, role: null, sucursalId: null, username: '' };
let estado = { estado: 'loading', me: PERFIL_VACIO, token: null };
let enCurso = null;
const oyentes = new Set();
const emitir = (nuevo) => { estado = nuevo; oyentes.forEach((f) => f()); };

export const suscribir = (f) => { oyentes.add(f); return () => oyentes.delete(f); };
export const leerSesion = () => estado;

/** Decodifica el payload del JWT solo para conocer su vencimiento (la validez real la decide la API). */
export function tokenVencido(token) {
  try {
    const p = JSON.parse(atob(String(token).split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    return !p?.exp || Date.now() / 1000 >= Number(p.exp);
  } catch { return true; }
}

const esperar = (ms) => new Promise((r) => setTimeout(r, ms));
// Reintentable: sin respuesta (red) o error del servidor. 401/403 no se reintentan.
const reintentable = (e) => !e?.status || e.status >= 500 || e.status === 408 || e.status === 429;

/** Carga (o reutiliza) la sesión del token actual. `forzar` vuelve a consultar (botón Reintentar). */
export function cargarSesion({ forzar = false } = {}) {
  const token = getToken();
  if (!token) { if (estado.estado !== 'unauthenticated') emitir({ estado: 'unauthenticated', me: PERFIL_VACIO, token: null }); return Promise.resolve(); }
  if (tokenVencido(token)) {
    diag('sesion', 'token vencido al restaurar');
    clearSession(); avisarSesionVencida();
    emitir({ estado: 'unauthenticated', me: PERFIL_VACIO, token: null, mensaje: SESION_VENCIDA });
    return Promise.resolve();
  }
  // Mismo token ya cargado (o cargándose): se reutiliza. Un token distinto (otro usuario) reinicia todo.
  if (!forzar && estado.token === token && estado.estado === 'authenticated') return Promise.resolve();
  if (!forzar && enCurso && estado.token === token) return enCurso;
  emitir({ estado: 'loading', me: PERFIL_VACIO, token });
  const actual = (async () => {
    let ultimo;
    for (let i = 0; i < ESPERAS_MS.length; i++) {
      if (ESPERAS_MS[i]) await esperar(ESPERAS_MS[i]);
      try {
        const p = await fetchMe();
        if (!p?.uid || !p?.role) throw Object.assign(new Error('Perfil incompleto'), { status: 500 });
        if (getToken() !== token) return; // cambió la sesión mientras tanto
        diag('sesion', 'perfil cargado', { rol: p.role, intento: i + 1 });
        emitir({ estado: 'authenticated', token, me: { uid: p.uid, role: p.role, sucursalId: p.sucursalId || null,
          sucursales: Array.isArray(p.sucursales) ? p.sucursales : [], username: p.username || '', superAdmin: Boolean(p.superAdmin) } });
        return;
      } catch (e) {
        ultimo = e;
        diag('sesion', 'fallo al cargar perfil', { status: e?.status || 'red', intento: i + 1 });
        if (e?.status === 401) { // token rechazado por la API: la sesión ya no es válida
          clearSession(); avisarSesionVencida();
          emitir({ estado: 'unauthenticated', me: PERFIL_VACIO, token: null, mensaje: SESION_VENCIDA });
          return;
        }
        if (!reintentable(e)) break;
      }
    }
    if (getToken() === token) emitir({ estado: 'error', me: PERFIL_VACIO, token, mensaje: ERROR_SESION, detalle: ultimo?.status || 'red' });
  })().finally(() => { if (enCurso === actual) enCurso = null; });
  enCurso = actual;
  return actual;
}

/** Solo para pruebas. */
export function _esperasReintento(ms) { ESPERAS_MS = ms; }
export function _reiniciarSesion() { estado = { estado: 'loading', me: PERFIL_VACIO, token: null }; enCurso = null; }
